// Admin API client. Reuses the existing token/request layer from lib/api.js
// and **automatically attaches the `X-Workspace` header** on every request
// so the backend can scope canonical reads/writes correctly.
//
// The workspace value comes from localStorage (see `lib/workspace.js`) so
// switching workspaces in the topbar dropdown is reflected immediately
// without re-login. Demo admins are coerced server-side anyway, so this
// header is best-effort UX, not a security boundary.
import { api } from '@/lib/api';
import { getWorkspace } from '@/lib/workspace';

const wsHeaders = () => ({ 'X-Workspace': getWorkspace() });

const get = (path) => api.get(path, { headers: wsHeaders() });
const post = (path, body) => api.post(path, body, { headers: wsHeaders() });
const patch = (path, body) => api.patch(path, body, { headers: wsHeaders() });
const del = (path) => api.del(path, { headers: wsHeaders() });
const postForm = (path, formData) => api.postForm(path, formData, { headers: wsHeaders() });

export const AdminAPI = {
  // Auth/identity
  me: () => get('/api/admin/me'),
  stats: () => get('/api/admin/stats'),

  // Banks
  banks: {
    list: ({ q, includeInactive } = {}) => {
      const sp = new URLSearchParams();
      if (q) sp.set('q', q);
      if (includeInactive) sp.set('include_inactive', 'true');
      const qs = sp.toString();
      return get(`/api/admin/banks${qs ? `?${qs}` : ''}`);
    },
    get: (id) => get(`/api/admin/banks/${id}`),
    create: (body) => post('/api/admin/banks', body),
    update: (id, body) => patch(`/api/admin/banks/${id}`, body),
    remove: (id) => del(`/api/admin/banks/${id}`),
    checkSlug: (slug, excludeId) => {
      const sp = new URLSearchParams();
      sp.set('slug', slug || '');
      if (excludeId) sp.set('exclude_id', excludeId);
      return get(`/api/admin/banks/check-slug?${sp.toString()}`);
    },
  },

  // Cards
  cards: {
    list: ({ q, bank_id, status, type } = {}) => {
      const sp = new URLSearchParams();
      if (q) sp.set('q', q);
      if (bank_id) sp.set('bank_id', bank_id);
      if (status) sp.set('status', status);
      if (type) sp.set('type', type);
      const qs = sp.toString();
      return get(`/api/admin/cards${qs ? `?${qs}` : ''}`);
    },
    get: (id) => get(`/api/admin/cards/${id}`),
    create: (body) => post('/api/admin/cards', body),
    update: (id, body) => patch(`/api/admin/cards/${id}`, body),
    remove: (id) => del(`/api/admin/cards/${id}`),
  },

  // Rule versions
  rules: {
    versions: (cardId) => get(`/api/admin/cards/${cardId}/rule-versions`),
    active: (cardId) => get(`/api/admin/cards/${cardId}/rule-versions/active`),
    version: (versionId) => get(`/api/admin/rule-versions/${versionId}`),
    createDraft: (cardId) => post(`/api/admin/cards/${cardId}/rule-versions/draft`, {}),
    patchDraft: (versionId, payload) => patch(`/api/admin/rule-versions/${versionId}`, payload),
    publish: (versionId, body = {}) => post(`/api/admin/rule-versions/${versionId}/publish`, body),
    archive: (versionId) => post(`/api/admin/rule-versions/${versionId}/archive`, {}),
    rollback: (versionId) => post(`/api/admin/rule-versions/${versionId}/rollback`, {}),
    diff: (cardId, a, b) => get(`/api/admin/cards/${cardId}/rule-versions/diff?a=${a}&b=${b}`),
  },

  // Simulator
  simulate: (body) => post('/api/admin/simulate', body),

  // Audit logs
  auditLogs: ({ entity_type, entity_id, actor_email, action, limit = 100 } = {}) => {
    const sp = new URLSearchParams();
    if (entity_type) sp.set('entity_type', entity_type);
    if (entity_id) sp.set('entity_id', entity_id);
    if (actor_email) sp.set('actor_email', actor_email);
    if (action) sp.set('action', action);
    sp.set('limit', String(limit));
    return get(`/api/admin/audit-logs?${sp.toString()}`);
  },

  // Routing hints (Phase 5 — Chrome extension intelligence)
  routingHints: {
    list: () => get('/api/admin/routing-hints'),
    get: (id) => get(`/api/admin/routing-hints/${id}`),
    create: (body) => post('/api/admin/routing-hints', body),
    update: (id, body) => patch(`/api/admin/routing-hints/${id}`, body),
    remove: (id) => del(`/api/admin/routing-hints/${id}`),
  },

  // Suggested routing hints (extension telemetry queue)
  routingSuggestions: {
    list: (status) => get(`/api/admin/routing-suggestions${status ? `?status=${status}` : ''}`),
    classify: (id) => post(`/api/admin/routing-suggestions/${id}/classify`, {}),
    promote: (id, override) => post(`/api/admin/routing-suggestions/${id}/promote`, override ? { override } : {}),
    dismiss: (id) => post(`/api/admin/routing-suggestions/${id}/dismiss`, {}),
    prune: (days = 90) => post(`/api/admin/routing-suggestions/prune?days=${days}`, {}),
  },

  // Phase 9 — Card asset pipeline (image upload → rembg → GPT-5 Vision)
  cardAssets: {
    upload: (cardId, file, { skipVision = false, processingMode = 'enhanced', orientation = 'auto' } = {}) => {
      const fd = new FormData();
      fd.append('file', file);
      const qs = new URLSearchParams();
      if (skipVision) qs.set('skip_vision', 'true');
      qs.set('processing_mode', processingMode);
      qs.set('orientation', orientation);
      return postForm(`/api/admin/cards/${cardId}/asset?${qs.toString()}`, fd);
    },
    reprocess: (cardId, { skipVision = false, processingMode = 'enhanced', orientation = 'auto' } = {}) => {
      const qs = new URLSearchParams();
      if (skipVision) qs.set('skip_vision', 'true');
      qs.set('processing_mode', processingMode);
      qs.set('orientation', orientation);
      return post(`/api/admin/cards/${cardId}/asset/reprocess?${qs.toString()}`, {});
    },
    remove: (cardId) => del(`/api/admin/cards/${cardId}/asset`),
  },
  // Phase 23.D \u2014 Context taxonomy (Rule Builder Advanced Conditions, etc.)
  contexts: {
    // Curated taxonomy registry. Public-but-auth-aware: anonymous = production.
    registry: () => api.get('/api/contexts/registry'),
    // Category-narrowed view used by Rule Builder Advanced Conditions section
    // to keep the dropdown manageable as taxonomy grows.
    relevant: (category) => {
      const qs = category ? `?category=${encodeURIComponent(category)}` : '';
      return api.get(`/api/contexts/relevant${qs}`);
    },
    // Admin CRUD on definitions (used by future curation UI; not by Rule Builder)
    listAdmin: ({ contextType, isActive } = {}) => {
      const sp = new URLSearchParams();
      if (contextType) sp.set('context_type', contextType);
      if (isActive !== undefined) sp.set('is_active', String(isActive));
      const qs = sp.toString();
      return get(`/api/admin/context-definitions${qs ? `?${qs}` : ''}`);
    },
    create: (body) => post('/api/admin/context-definitions', body),
    update: (id, body) => patch(`/api/admin/context-definitions/${id}`, body),
    remove: (id, { hard = false } = {}) => {
      const qs = hard ? '?hard=true' : '';
      return del(`/api/admin/context-definitions/${id}${qs}`);
    },
    reactivate: (id) => post(`/api/admin/context-definitions/${id}/reactivate`, {}),
  },

  // Categories (Phase-25) — canonical category registry.
  // Consumed by Rule Builder dropdowns, Recommend page, Simulator, Extension.
  // Admin-only mutations; read endpoint is public via `/api/categories`.
  categories: {
    list: ({ includeInactive = true } = {}) => {
      const sp = new URLSearchParams();
      if (includeInactive) sp.set('include_inactive', 'true');
      const qs = sp.toString();
      return get(`/api/admin/categories${qs ? `?${qs}` : ''}`);
    },
    usage: () => get('/api/admin/categories/usage'),
    orphans: () => get('/api/admin/categories/orphans'),
    create: (body) => post('/api/admin/categories', body),
    update: (id, body) => patch(`/api/admin/categories/${id}`, body),
    rename: (id, newValue) => post(`/api/admin/categories/${id}/rename`, { new_value: newValue }),
    merge: (id, targetValue, addAlias = true) =>
      post(`/api/admin/categories/${id}/merge`, { target_value: targetValue, add_alias: addAlias }),
    // Phase-25.A.1 — orphan-specific operations (no canonical id available).
    mergeOrphan: (sourceValue, targetValue, addAlias = true) =>
      post('/api/admin/categories/merge-orphan', {
        source_value: sourceValue,
        target_value: targetValue,
        add_alias: addAlias,
      }),
    stripOrphan: (sourceValue) =>
      post('/api/admin/categories/strip-orphan', { source_value: sourceValue }),
    remove: (id, { hard = false } = {}) => {
      const qs = hard ? '?hard=true' : '';
      return del(`/api/admin/categories/${id}${qs}`);
    },
  },

  // Users (customer base) — read-only admin view. Backed by
  // /api/admin/users which aggregates cards owned, recommend queries,
  // gmail-linked flag, and last-activity per user in a single roundtrip.
  users: {
    list: ({ q, role, auth, limit = 200 } = {}) => {
      const sp = new URLSearchParams();
      if (q) sp.set('q', q);
      if (role && role !== 'all') sp.set('role', role);
      if (auth && auth !== 'all') sp.set('auth', auth);
      sp.set('limit', String(limit));
      return get(`/api/admin/users?${sp.toString()}`);
    },
    get: (userId) => get(`/api/admin/users/${userId}`),
  },
};

// Re-export the form-data helper for the file-upload (PDF ingestion) flow.
export const adminPostForm = postForm;
